import { useCallback, useEffect, useState } from 'react';
import * as api from './notificationsApi';
import {
  NotificationsForbiddenError,
  NotificationsUnavailableError,
  notificationsErrorMessage,
} from './notificationsApi';
import { mailReady, quietHoursOf, quietHoursPatch, subscriptionsInOrder } from './format';
import type {
  MailSettings,
  NotificationKind,
  NotificationLogView,
  SubscriptionView,
} from '../../types/notifications';

export interface LoadState {
  loading: boolean;
  /** One short line, already human-readable */
  error: string | null;
  /** The endpoints do not exist on this server yet */
  unavailable: boolean;
}

const failure = (error: any): Pick<LoadState, 'error' | 'unavailable'> => ({
  error: notificationsErrorMessage(error),
  unavailable: error instanceof NotificationsUnavailableError,
});

/* ------------------------------------------------------------------ */
/* A person's own preferences                                          */
/* ------------------------------------------------------------------ */

export function usePreferences() {
  const [subscriptions, setSubscriptions] = useState<SubscriptionView[]>([]);
  const [state, setState] = useState<LoadState>({ loading: true, error: null, unavailable: false });
  const [busy, setBusy] = useState<NotificationKind | 'quiet' | null>(null);

  const reload = useCallback(async () => {
    setState((current) => ({ ...current, loading: true }));
    try {
      setSubscriptions(subscriptionsInOrder(await api.fetchPreferences()));
      setState({ loading: false, error: null, unavailable: false });
    } catch (error) {
      setState({ loading: false, ...failure(error) });
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  /** The switch moves first; a refusal puts it back and says why */
  const toggle = useCallback(
    async (kind: NotificationKind, enabled: boolean) => {
      const previous = subscriptions;
      const quiet = quietHoursOf(subscriptions);
      setSubscriptions((current) => current.map((sub) => (sub.kind === kind ? { ...sub, enabled } : sub)));
      setBusy(kind);
      setState((current) => ({ ...current, error: null }));
      try {
        const next = await api.savePreference({
          kind,
          channel: 'email',
          enabled,
          quietFrom: quiet.from,
          quietTo: quiet.to,
        });
        setSubscriptions(subscriptionsInOrder(next));
      } catch (error) {
        setSubscriptions(previous);
        setState((current) => ({ ...current, error: notificationsErrorMessage(error) }));
      } finally {
        setBusy(null);
      }
    },
    [subscriptions],
  );

  const saveQuietHours = useCallback(
    async (from: number | null, to: number | null) => {
      const previous = subscriptions;
      setBusy('quiet');
      setState((current) => ({ ...current, error: null }));
      try {
        const next = await api.savePreferences(quietHoursPatch(subscriptions, from, to));
        setSubscriptions(subscriptionsInOrder(next));
      } catch (error) {
        setSubscriptions(previous);
        setState((current) => ({ ...current, error: notificationsErrorMessage(error) }));
      } finally {
        setBusy(null);
      }
    },
    [subscriptions],
  );

  return { subscriptions, ...state, busy, toggle, saveQuietHours, reload };
}

/* ------------------------------------------------------------------ */
/* What was actually sent                                              */
/* ------------------------------------------------------------------ */

export function useHistory() {
  const [entries, setEntries] = useState<NotificationLogView[]>([]);
  const [state, setState] = useState<LoadState>({ loading: true, error: null, unavailable: false });

  const reload = useCallback(async () => {
    setState((current) => ({ ...current, loading: true }));
    try {
      setEntries(await api.fetchHistory());
      setState({ loading: false, error: null, unavailable: false });
    } catch (error) {
      setState({ loading: false, ...failure(error) });
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  return { entries, ...state, reload };
}

/* ------------------------------------------------------------------ */
/* Whether mail can leave the building at all                          */
/* ------------------------------------------------------------------ */

/**
 * `unknown` is its own answer: only an admin may read the mail settings, so for
 * everyone else the page says nothing rather than guessing.
 */
export type MailStatus = 'loading' | 'ready' | 'not-configured' | 'unavailable' | 'unknown';

export function useMailStatus(): MailStatus {
  const [status, setStatus] = useState<MailStatus>('loading');

  useEffect(() => {
    let cancelled = false;
    api
      .fetchMailSettings()
      .then((settings) => {
        if (!cancelled) setStatus(mailReady(settings) ? 'ready' : 'not-configured');
      })
      .catch((error) => {
        if (cancelled) return;
        if (error instanceof NotificationsUnavailableError) setStatus('unavailable');
        else if (error instanceof NotificationsForbiddenError) setStatus('unknown');
        else setStatus('unknown');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return status;
}

/* ------------------------------------------------------------------ */
/* Mail settings, for admins                                           */
/* ------------------------------------------------------------------ */

export function useMailSettings(enabled = true) {
  const [settings, setSettings] = useState<MailSettings | null>(null);
  const [state, setState] = useState<LoadState>({ loading: true, error: null, unavailable: false });

  const reload = useCallback(async () => {
    // Only an admin may read these; asking anyway would answer 403, not settings
    if (!enabled) return;
    setState((current) => ({ ...current, loading: true }));
    try {
      setSettings(await api.fetchMailSettings());
      setState({ loading: false, error: null, unavailable: false });
    } catch (error) {
      setState({ loading: false, ...failure(error) });
    }
  }, [enabled]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { settings, setSettings, ...state, reload };
}
